import { useEffect, useState } from "react";
import { Loader2, Plus, Undo2, X } from "lucide-react";

import type { Camera } from "../../api";
import { FisheyeView } from "../../components/FisheyeView";
import type { CalibratedLens, Pose } from "../../fisheye/lens";
import { grabLiveFrame, recorderSnapshot, viewLabel, type Picture } from "./FisheyeDialog";

export type DraftCustomView = Pose & { name: string };

const FREE_START: Pose = { yaw: 0, tilt: 45, fov: 75 };

/** Custom AI views of a calibrated fisheye, framed in a free look-around of a
 *  picture of the camera. The frame is 16:9, the shape of an AI view, so what
 *  is framed here is what the detector analyses. Nothing is written until the
 *  camera is saved. */
export function FisheyeCustomViews({
  camera,
  lens,
  existing,
  removed,
  onToggleRemove,
  pending,
  onAdd,
  onRemovePending,
}: {
  camera: Camera;
  lens: CalibratedLens;
  existing: Camera[];
  removed: Set<string>;
  onToggleRemove: (id: string) => void;
  pending: DraftCustomView[];
  onAdd: (view: DraftCustomView) => void;
  onRemovePending: (index: number) => void;
}) {
  const [framing, setFraming] = useState(false);
  const [picture, setPicture] = useState<Picture | null>(null);
  const [error, setError] = useState("");
  const [pose, setPose] = useState<Pose>(FREE_START);
  const [name, setName] = useState("");

  useEffect(() => {
    if (!framing || picture) return;
    let cancelled = false;
    (camera.nvr_id ? recorderSnapshot(camera.id) : grabLiveFrame(camera.id)).then(
      (pic) => !cancelled && setPicture(pic),
      (e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      cancelled = true;
    };
  }, [framing, picture, camera.id, camera.nvr_id]);

  return (
    <div className="space-y-2">
      <span className="block text-xs text-bf-muted">Custom views</span>
      {(existing.length > 0 || pending.length > 0) && (
        <ul className="space-y-1">
          {existing.map((v) => {
            const off = removed.has(v.id);
            return (
              <li
                key={v.id}
                className="flex items-center gap-2 rounded-md border border-white/10 px-2 py-1 text-xs"
              >
                <span className={`flex-1 truncate ${off ? "text-bf-muted line-through" : ""}`}>
                  {viewLabel(camera, v)}
                </span>
                <button
                  type="button"
                  className="text-bf-muted hover:text-white"
                  onClick={() => onToggleRemove(v.id)}
                  aria-label={off ? `Keep ${viewLabel(camera, v)}` : `Remove ${viewLabel(camera, v)}`}
                >
                  {off ? <Undo2 size={13} /> : <X size={13} />}
                </button>
              </li>
            );
          })}
          {pending.map((v, i) => (
            <li
              key={`pending-${i}`}
              className="flex items-center gap-2 rounded-md border border-bf-magenta/40 bg-bf-magenta/10 px-2 py-1 text-xs"
            >
              <span className="flex-1 truncate">{v.name}</span>
              <span className="font-mono text-[11px] text-bf-muted">
                {v.yaw.toFixed(0)}° / {v.tilt.toFixed(0)}° / {v.fov.toFixed(0)}°
              </span>
              <button
                type="button"
                className="text-bf-muted hover:text-white"
                onClick={() => onRemovePending(i)}
                aria-label={`Remove ${v.name}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {framing ? (
        <div className="space-y-2">
          <div className="relative aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
            {error ? (
              <div className="absolute inset-0 grid place-items-center p-3 text-center text-xs text-bf-muted">
                {error}
              </div>
            ) : picture ? (
              <FisheyeView
                lens={lens}
                image={picture.source}
                views={[pose]}
                interactive
                onChange={(v) => setPose(v[0])}
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-bf-muted">
                <Loader2 className="animate-spin" size={18} />
              </div>
            )}
          </div>
          <p className="text-xs text-bf-muted">
            Drag to look around, scroll to zoom, click the small fisheye to aim there.
          </p>
          <div className="flex gap-2">
            <input
              className="bf-input flex-1"
              placeholder="View name, e.g. Entrance"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
            <button
              type="button"
              className="bf-button disabled:opacity-40"
              disabled={!picture || !name.trim()}
              onClick={() => {
                onAdd({ ...pose, name: name.trim() });
                setName("");
              }}
            >
              <Plus size={15} className="mr-1" /> Add view
            </button>
            <button type="button" className="bf-button bg-white/10 bg-none" onClick={() => setFraming(false)}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="bf-button bg-white/10 bg-none text-xs" onClick={() => setFraming(true)}>
          <Plus size={14} className="mr-1" /> Frame a custom view
        </button>
      )}
    </div>
  );
}
